import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { postGenerateMbr, type GenerateMbrResult } from '../api';
import { fiscalLabelFor, previousPeriod, type MonthOption } from '../filters';

/**
 * AOZONE-48 (WS4-4): "Generate MBR" button + inline result banner.
 *
 * AOZONE-60: adds an explicit period picker + "Combine with previous adjacent
 * month" checkbox. Combine is a doc-gen-time filter only — entries themselves
 * always carry a single-month mbrPeriod. When checked, a preview label shows
 * the two fiscal labels the resulting doc will span.
 *
 * On click, POSTs /api/mbr/generate with { period, combineWithPrevious } and
 * renders a small status/alert banner based on the discriminated result:
 *   - ok                 → green status with counts + "Open MBR doc" link
 *   - consent_required   → status banner + window.location.assign(consentUrl)
 *   - nothing_to_generate → red alert (server-side guard tripped)
 *   - error              → red alert with server message + orphanCopyId
 */
export interface GenerateMbrButtonProps {
  includedCount: number;
  period: string;
  availableMonths: MonthOption[];
}

export function GenerateMbrButton({
  includedCount,
  period: initialPeriod,
  availableMonths,
}: GenerateMbrButtonProps) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<GenerateMbrResult | null>(null);
  const [period, setPeriod] = useState(initialPeriod);
  const [combineWithPrevious, setCombineWithPrevious] = useState(false);

  const disabled = loading || includedCount === 0;
  const emptyTitle =
    includedCount === 0
      ? 'Mark at least one entry as Include in MBR to generate.'
      : undefined;

  const onClick = async () => {
    setLoading(true);
    setResult(null);
    const r = await postGenerateMbr({ period, combineWithPrevious });
    setResult(r);
    setLoading(false);
    if (r.kind === 'consent_required') {
      window.location.assign(r.consentUrl);
    }
  };

  const previewLabel = combineWithPrevious
    ? `${fiscalLabelFor(previousPeriod(period))} / ${fiscalLabelFor(period)}`
    : null;

  return (
    <div className="flex flex-col items-end gap-3 py-2">
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-slate-700">
          Period
          <select
            aria-label="Period"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm"
          >
            {availableMonths.map((m) => (
              <option key={m.key} value={m.key}>
                {m.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={combineWithPrevious}
            onChange={(e) => setCombineWithPrevious(e.target.checked)}
          />
          Combine with previous adjacent month
        </label>
        <Button onClick={onClick} disabled={disabled} title={emptyTitle}>
          {loading ? 'Generating…' : 'Generate MBR'}
        </Button>
      </div>
      {previewLabel && (
        <div className="text-xs text-slate-500">
          Will include: {previewLabel}
        </div>
      )}
      {result && result.kind === 'ok' && (
        <div
          role="status"
          className="flex w-full items-center justify-between gap-3 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900"
        >
          <span>
            MBR generated · {result.includedCount} included, {result.skippedCount} skipped ·{' '}
            <a
              href={result.docUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium underline underline-offset-2 hover:text-emerald-700"
            >
              Open MBR doc
            </a>
          </span>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => setResult(null)}
            className="rounded px-2 py-1 text-emerald-700 hover:bg-emerald-100"
          >
            Dismiss
          </button>
        </div>
      )}
      {result && result.kind === 'consent_required' && (
        <div
          role="status"
          className="w-full rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"
        >
          Google Docs permission required. Redirecting…
        </div>
      )}
      {result && result.kind === 'nothing_to_generate' && (
        <div
          role="alert"
          className="w-full rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
        >
          Mark at least one entry as 'Include in MBR' to generate.
        </div>
      )}
      {result && result.kind === 'error' && (
        <div
          role="alert"
          className="w-full rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
        >
          <div>{result.message}</div>
          {result.orphanCopyId && (
            <div className="mt-1 text-xs text-red-700">
              Orphan Google Doc copy left behind — clean up: {result.orphanCopyId}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
